import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import TooltipSeason from '../../../components/tooltip-season/tooltip-season.component';
import { COLORS, isPastThreshold } from '../../../utilities';

function getWPNDColor(value) {
  if (value < 1) {
    return COLORS.green;
  } else if (value < 2) {
    return COLORS.yellow;
  } else if (value < 3) {
    return COLORS.orange;
  } else {
    return COLORS.darkRed;
  }
}

const bandsInfo = [{
  color: COLORS.darkGreenRgb,
  from: -100,
  to: 1,
  labelText: 'Healthy'
},{
  color: COLORS.yellowRgb,
  from: 1,
  to: 2,
  labelText: 'Mild Infection'
},{
  color: COLORS.darkOrangeRgb,
  from: 2,
  to: 3,
  labelText: 'Moderate Infection'
},{
  color: COLORS.darkRedRgb,
  from: 3,
  to: 100,
  labelText: 'Severe Infection'
}];
function createPlotObjects(value) {
  const plotBands = bandsInfo.map(({ color, to, from, labelText}) => ({
    color: `rgba(${color}, ${(from <= value && value < to) ? 0.1 : 0})`,
    from,
    to,
    label: {
      text: labelText,
      verticalAlign: 'middle',
      align: 'left',
      x: 30,
      y: 3,
      style: {
        color: (from <= value && value < to) ? 'black' : COLORS.darkGray
      }
    }
  }));

  const plotLines = bandsInfo.map(({ to }) => ({
    width: 1,
    value: to,
    color: COLORS.lightGray
  }));
  
  return { plotBands, plotLines};
}

export function whitePineNeedleDamageOptions(chartData, address) {
  const { years, defoliationSeverity } = chartData;

  const yPadding = 0.2;
  let max = -Infinity;
  let min = Infinity;

  const data = defoliationSeverity.map(value => {
    if (max < value) max = value;
    if (value < min) min = value;
    return {
      y: value,
      marker: {
        fillColor: getWPNDColor(value),
        lineColor: 'black',
        lineWidth: 1,
        radius: 3
      }
    }
  });

  const { plotBands, plotLines } = createPlotObjects(data.slice(-1)[0].y);

  return {
    chart: {
      marginRight: 20,
    },
    title: {
      text: `Predicted Defoliation Severity ${years[0]}-${years[years.length - 1]}`,
      margin: 18
    },
    subtitle: {
      text: `Location: ${address}`,
      y: 34
    },
    series: [
      {
        data,
        name: 'Predicted Defoliation Severity Timeseries',
        color: 'black'
      }
    ],
    xAxis: {
      categories: years,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Year',
      },
      lineWidth: 0
    },
    yAxis: [
      {
        min: min - yPadding,
        max: max + yPadding,
        startOnTick: true,
        endOnTick: false,
        gridLineWidth: 0,
        tickInterval: 1,
        title: {
          text: 'Predicted Defoliation Severity',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        },
        plotBands,
        plotLines
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';
        const point = this.points[0];
        const value = Math.round(point.y * 10) / 10;
        const band = bandsInfo.find(bandInfo => value < bandInfo.to);
        const color = point.point.marker.fillColor;

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={point.x} color={color} />

            <div>
              <div className='mc-season'>{band.labelText}</div>
              <div className='wpnd-container'>
                <div className='wpnd-number' style={{ textAlign: 'center' }}>{value.toFixed(1)}</div>
              </div>
            </div>
          </div>
        );
      },
    },
  };
}

export function southernPineBeetlePastYearsOptions(chartData, address, seasonBounds) {
  const { years, toDate, toDateDate, fullSeason } = chartData.yearlySeasons;
  const min = 0;
  let max = Math.max(...fullSeason, ...toDate)*1.05;

  if (typeof seasonBounds === 'function') {
    seasonBounds = seasonBounds();
  }

  const seasonSDate = seasonBounds[0].slice(5,10).replace("-","/");
  const seasonEDate = seasonBounds[1].slice(5,10).replace("-","/");

  const finalCurrentYearDate = chartData.timeseries.dates.slice(-1)[0];
  let finalDataDate = new Date(finalCurrentYearDate) < new Date(toDateDate) ? finalCurrentYearDate : toDateDate;
  finalDataDate = finalDataDate.slice(5,10).replace('-','/');

  console.log(years, chartData);

  return {
    chart: {
      marginRight: 20,
      spacingBottom: 17,
      type: 'column'
    },
    plotOptions: {
      column: {
        grouping: false
      }
    },
    title: {
      text: `Number of Times Min. Temperature was <${chartData.threshold}°F for the Season: ${seasonSDate} to ${seasonEDate}`,
      margin: 18
    },
    subtitle: {
      text: `Location: ${address}`,
      y: 34
    },
    series: [
      {
        data: fullSeason,
        name: 'At End of Season',
        color: COLORS.lightGray,
        opacity: 0.6,
        borderColor: COLORS.lightGray,
        groupPadding: 0
      },{
        data: toDate,
        name: 'To Date',
        color: COLORS.lightBlue,
        pointPadding: 0.125
      }
    ],
    xAxis: {
      categories: years,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Year',
      },
    },
    yAxis: [
      {
        min,
        max,
        startOnTick: true,
        tickInterval: Math.max(1,Math.floor(((max - min) / 5))),
        gridLineWidth: 0,
        title: {
          text: '# of Pest Killing Events',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        },
        plotLines: [
          {
            color: 'rgb(150,150,150)',
            value: Math.round(toDate.slice(0, -1).reduce((a,b) => a + b) / (toDate.length - 1)),
            label: {
              text: 'To Date Normal',
              style: {
                color: 'rgb(80,80,80)',
              },
            },
            zIndex: 4
          },
        ],
      },
    ],
    legend: {
      enabled: false
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';

        const date = this.points[0].x;
        const numEvents = this.points[0].y;
        const isCurrentYear = date === years.slice(-1)[0];

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={numEvents === 0 ? COLORS.darkRed : COLORS.lightBlue} />

            {numEvents === 0 ?
              <div className='mc-season warning'>Potential emergence</div>
              :
              <div className='mc-season'>Emergence unlikely</div>
            }

            {this.points.reverse().map(point => (
              <div key={point.y} className='mc-container' style={{ justifyContent: 'space-between' }}>
                <div className='mc-units'>{point.series.name === 'To Date' ? `To ${isCurrentYear ? finalDataDate : toDateDate.slice(5,10).replace('-','/')}` : point.series.name}: </div>
                <div className='mc-number'>{point.y}</div>
              </div>
            ))}
          </div>
        );
      },
    },
  };
}

export function pestsAndPathogensTimeseriesOptions(chartData, address, threshold, direction) {
  const compFn = isPastThreshold(threshold, direction);
  const timeseries = chartData.timeseries.current.map(temp => compFn(temp) ? temp : null);
  const dates = chartData.timeseries.dates.slice(0, timeseries.length).map(str => str.slice(5));

  let min = 0;
  let max = 0;
  let zones = [];
  if (direction === 'below') {
    min = Math.min(...timeseries, threshold - 5);
    max = threshold + 5;
    zones = [{
      value: threshold,
      color: COLORS.lightBlue
    },{
      color: COLORS.darkRed
    }];
  } else {
    min = threshold - 5;
    max = Math.max(...timeseries, threshold + 5);
    zones = [{
      color: COLORS.darkRed
    },{
      value: threshold,
      color: COLORS.lightBlue
    }];
  }

  return {
    chart: {
      marginRight: 20,
      type: 'column'
    },
    title: {
      text: `Days with Min. Temperature <${chartData.threshold}°F Throughout Season`,
    },
    subtitle: {
      text: `Location: ${address}`,
    },
    series: [
      {
        data: timeseries,
        name: 'Current Season',
        groupPadding: 0,
        pointPadding: 0,
        threshold,
        zones
      }
    ],
    xAxis: {
      categories: dates,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Day of Year',
      },
    },
    yAxis: [
      {
        min,
        max,
        startOnTick: false,
        endOnTick: false,
        tickAmount: 5,
        gridLineWidth: 0,
        title: {
          text: 'Temperature (°F)',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        },
        plotLines: [
          {
            color: 'rgb(200,200,200)',
            value: threshold,
            label: {
              text: `${threshold}°F`,
              align: 'right',
              style: {
                color: 'rgb(150,150,150)',
              },
            },
          },
        ],
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';

        const point = this.points[0];
        const val = point.y;
        const date = point.x;

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={compFn(val) ? COLORS.lightBlue : COLORS.darkRed} />

            <div className='mc-container'>
              <div className='mc-number'>{Math.round(val * 100) / 100}</div>
              <div className='mc-units'>°F</div>
            </div>
          </div>
        );
      },
    },
  };
}

export function pestsAndPathogensAnnualOptions(chartData, address, seasonBounds) {
  const { years, toDate, toDateDate, fullSeason } = chartData.yearlySeasons;
  const min = 0;
  const max = Math.max(...fullSeason, ...toDate);

  if (typeof seasonBounds === 'function') {
    seasonBounds = seasonBounds();
  }

  const seasonSDate = seasonBounds[0].slice(5,10).replace("-","/");
  const seasonEDate = seasonBounds[1].slice(5,10).replace("-","/");

  const finalCurrentYearDate = chartData.timeseries.dates.slice(-1)[0];
  let finalDataDate = new Date(finalCurrentYearDate) < new Date(toDateDate) ? finalCurrentYearDate : toDateDate;
  finalDataDate = finalDataDate.slice(5,10).replace('-','/');

  return {
    chart: {
      marginRight: 20,
      type: 'column'
    },
    plotOptions: {
      column: {
        grouping: false
      }
    },
    title: {
      text: `Number of Times Min. Temperature was <${chartData.threshold}°F for the Season: ${seasonSDate} to ${seasonEDate}`,
      margin: 18
    },
    subtitle: {
      text: `Location: ${address}`,
      y: 34
    },
    series: [
      {
        data: fullSeason,
        name: 'At End of Season',
        color: COLORS.lightGray,
        opacity: 0.6,
        borderColor: COLORS.lightGray,
        groupPadding: 0
      },{
        data: toDate,
        name: 'To Date',
        color: COLORS.lightBlue,
        pointPadding: 0.125
      }
    ],
    xAxis: {
      categories: years,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Year',
      },
    },
    yAxis: [
      {
        min,
        max,
        startOnTick: false,
        endOnTick: false,
        tickAmount: 5,
        gridLineWidth: 0,
        title: {
          text: '# of Pest Killing Events',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        },
        plotLines: [
          {
            color: 'rgb(150,150,150)',
            value: Math.round(toDate.slice(0, -1).reduce((a,b) => a + b) / (toDate.length - 1)),
            label: {
              text: 'To Date Normal',
              style: {
                color: 'rgb(80,80,80)',
              },
            },
            zIndex: 4
          },
        ],
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';

        const date = this.points[0].x;
        const isCurrentYear = date === years.slice(-1)[0];

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={COLORS.lightBlue} />

            {this.points.reverse().map(point => (
              <div key={point.y} className='mc-container' style={{ justifyContent: 'space-between' }}>
                <div className='mc-units'>{point.series.name === 'To Date' ? `To ${isCurrentYear ? finalDataDate : toDateDate.slice(5,10).replace('-','/')}` : point.series.name}: </div>
                <div className='mc-number'>{point.y}</div>
              </div>
            ))}
          </div>
        );
      },
    },
  };
}

export function beechLeafDiseaseOptions(chartData, address, variableName) {
  const seriesData = chartData[`${variableName.toLowerCase()}Probabilities`];

  return {
    chart: {
      marginRight: 20,
      type: 'column'
    },
    title: {
      text: `Probability of Beech Leaf Disease Based on ${variableName}`,
    },
    subtitle: {
      text: `Location: ${address}`,
    },
    series: [
      {
        data: seriesData,
        pointPadding: 0,
        groupPadding: 0
      }
    ],
    xAxis: {
      categories: chartData.years,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Year',
      }
    },
    yAxis: [
      {
        min: 0,
        max: 100,
        startOnTick: true,
        endOnTick: true,
        gridLineWidth: 0,
        title: {
          text: 'Probability of Disease (%)',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          }
        },
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';
        const date = this.points[0].x;
        const value = this.points[0].y;
        const color = this.points[0].color;

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={color} />
            <div className='mc-container' style={{ justifyContent: 'space-between' }}>
              <div>{value}% chance of Beech Leaf Disease</div>
            </div>
          </div>
        );
      },
    },
  };
}

export function hwaDailyMintOptions(chartData, address, rest) {
  const { dates, values, threshold } = chartData.dailyMints;
  const season = chartData.season;

  // find indices of threshold crossing and place lines at those points
  const killEventLines = values.reduce((acc,v,i) => {
    if (v <= threshold) {
      acc.push({
        // color: 'rgb(200,200,200)',
        color: COLORS.green,
        value: i,
        dashStyle: 'dash'
        // label: {
        //   text: 'Kill Event',
        //   align: 'right',
        //   style: {
        //     color: 'rgb(150,150,150)',
        //   },
        // },
      });
    }
    return acc;
  }, []);

  return {
    chart: {
      marginRight: 20
    },
    title: {
      text: `Daily Minimum Temperature throughout the ${season} Season`,
    },
    subtitle: {
      text: `Location: ${address}`,
    },
    series: [
      {
        data: values,
        name: 'Current Season'
      }
    ],
    xAxis: {
      categories: dates,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Date',
      },
      plotLines: killEventLines
    },
    yAxis: [
      {
        startOnTick: false,
        endOnTick: false,
        tickAmount: 5,
        gridLineWidth: 0,
        title: {
          text: 'Temperature (°F)',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        },
        plotLines: [
          {
            color: 'rgb(200,200,200)',
            value: threshold,
            label: {
              text: `Threshold: ${threshold}°F`,
              align: 'right',
              style: {
                color: 'rgb(150,150,150)',
              },
            },
          },
        ]
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this || !this.points) return '';

        const point = this.points[0];
        const val = point.y;
        const date = point.x;
        const isKillEvent = val <= threshold

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={COLORS.lightBlue} />
            
            {isKillEvent &&
              <div className='mc-season-good'>Kill event occurred</div>
            }

            <div className='mc-container'>
              <div className='mc-number'>{Math.round(val * 100) / 100}</div>
              <div className='mc-units'>°F</div>
            </div>
          </div>
        );
      },
    },
  };
}

export function hwaAnnualKillOptions(chartData, address) {
  const { dates, values: avgtValues, threshold: avgtThreshold } = chartData.annualAvgts;
  const { values: mintsByYear, threshold: mintCntThreshold } = chartData.mintsByYear;
  const { values: mintValues, threshold: mintThreshold } = chartData.annualMints;

  const avgtColoredValues = [];
  const mintCntColoredValues = [];
  const mintColoredValues = [];
  const killEvents = [];
  for (let i = 0; i < avgtValues.length; i++) {
    const isKillFromAvgt = avgtValues[i] < avgtThreshold;
    const mintCnt = mintsByYear[i].reduce((cnt, v) => v <= mintCntThreshold ? cnt + 1 : cnt, 0);
    const isKillFromMintCnt = mintCnt >= 93;
    const isKillFromMint = mintValues[i] < mintThreshold;
    avgtColoredValues.push({ y: avgtValues[i], color: isKillFromAvgt ? COLORS.green : COLORS.lightRed });
    mintCntColoredValues.push({ y: isKillFromMintCnt ? 1 : 0, cnt: mintCnt, color: isKillFromMintCnt ? COLORS.lightBlue : 'transparent' });
    mintColoredValues.push({ y: mintValues[i], color: isKillFromMint ? COLORS.green : COLORS.lightRed });
    killEvents.push((isKillFromAvgt || isKillFromMint || isKillFromMintCnt) ? 0.95 : 0);
  }

  return {
    chart: {
      marginRight: 20
    },
    title: {
      text: 'Annual Kill Events and Winter Temperatures',
    },
    subtitle: {
      text: `Location: ${address}`,
    },
    series: [{
      data: avgtColoredValues,
      name: 'Mean Winter Temp.',
      color: COLORS.darkGray,
      zIndex: 1,
      marker: {
        lineWidth: 1,
        lineColor: COLORS.darkGray
      },
      legendIndex: 0
    },{
      data: mintColoredValues,
      name: 'Lowest Winter Temp.',
      color: 'black',
      zIndex: 2,
      marker: {
        lineWidth: 0,
        symbol: 'triangle',
        radius: 4
      },
      legendIndex: 2
    },{
      data: killEvents,
      name: 'Kill Event This Season',
      color: `rgba(${COLORS.greenRgb}, 0.25)`,
      zIndex: 0,
      type: 'column',
      groupPadding: 0,
      pointPadding: 0,
      yAxis: 1,
      legendIndex: 3
    },{
      data: mintCntColoredValues,
      name: 'Min. Temp. Kill Event',
      type:'scatter',
      yAxis: 1,
      showInLegend:true,
      marker: {
        symbol: 'triangle-down'
      },
      legendIndex: 1
    }],
    xAxis: {
      categories: dates,
      crosshair: {
        color: 'rgb(220,220,220)',
        width: 0.5,
      },
      title: {
        text: 'Year',
      }
    },
    yAxis: [
      {
        height: '95%',
        startOnTick: false,
        endOnTick: false,
        tickAmount: 5,
        gridLineWidth: 0,
        title: {
          text: 'Temperature (°F)',
        },
        labels: {
          style: {
            fontFamily:
              'Roboto, "Helvetica Neue", Verdana, Arial, Helvetica, sans-serif',
          },
        }
      },{
        min: 0,
        max: 1,
        visible: false
      }
    ],
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: 'white',
      formatter: function () {
        if (!this) return null;

        const date = this.x;
        const dateIndex = dates.findIndex(d => d === date);
        if (dateIndex < 0) return null;

        const meanWinterTempObj = avgtColoredValues[dateIndex];
        const daysUnderCountObj = mintCntColoredValues[dateIndex];
        const absMinTempObj = mintColoredValues[dateIndex];
        const isKillEvent = killEvents[dateIndex] > 0;

        return renderToStaticMarkup(
          <div className='tooltip-content-container'>
            <TooltipSeason year={date} color={isKillEvent ? COLORS.green : COLORS.lightRed} />

            {isKillEvent ?
              <div className='mc-season'>Kill event occurred</div>
              :
              <div className='mc-season warning'>No kill event</div>
            }

            <div className='mc-container' style={{ justifyContent: 'space-between' }}>
              <div className='mc-units'>Mean Winter Temp.: </div>
              <div className='mc-number' style={{ color: meanWinterTempObj.color }}>{Math.round(meanWinterTempObj.y * 100) / 100} °F</div>
            </div>

            <div className='mc-container' style={{ justifyContent: 'space-between' }}>
              <div className='mc-units'>Days Below Threshold: </div>
              <div className='mc-number' style={{ color: daysUnderCountObj.cnt < 93 ? COLORS.lightRed : COLORS.green }}>{daysUnderCountObj.cnt} days</div>
            </div>

            <div className='mc-container' style={{ justifyContent: 'space-between', gap: '4px' }}>
              <div className='mc-units'>Lowest Winter Temperature: </div>
              <div className='mc-number' style={{ color: absMinTempObj.color }}>{Math.round(absMinTempObj.y * 100) / 100} °F</div>
            </div>
          </div>
        );
      },
    },
  };
}